import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { listingService } from '../lib/databaseService';
import { ArrowLeft, Edit3, Trash2, Plus, Building2, ListChecks, Image, MapPin, Phone, DollarSign } from 'lucide-react';

export default function AgentCabinet({ user, agencies = [], onRefreshListings }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myListings, setMyListings] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // İstifadəçinin agentliyini tap
  const userAgency = agencies.find(a => a.id === user?.agencyId);

  const loadMyListings = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const listings = await listingService.getByUserId(user.id);
      setMyListings(listings);
    } catch (error) {
      console.error('Elanları yükləmə xətası:', error);
      toast.error('Elanları yükləmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadMyListings();
  }, [loadMyListings]);

  // Düzəliş modalını aç
  const openEditModal = (listing) => {
    setSelectedListing(listing);
    setEditForm({
      title: listing.title || '',
      description: listing.description || '',
      type: listing.type || '',
      category: listing.category || 'sale',
      price: listing.price || '',
      rooms: listing.rooms || '',
      area: listing.area || '',
      floor: listing.floor || '',
      total_floors: listing.total_floors || '',
      location: listing.location || '',
      address: listing.address || '',
      phone: listing.phone || '',
    });
    setEditModalOpen(true);
  };

  // Silmə modalını aç
  const openDeleteModal = (listing) => {
    setSelectedListing(listing);
    setDeleteModalOpen(true);
  };

  // Elanı yenilə
  const handleUpdateListing = async () => {
    if (!selectedListing?.id) return;

    try {
      setSaving(true);
      await listingService.updateFull(selectedListing.id, {
        ...editForm,
        images: selectedListing.images,
        amenities: selectedListing.amenities,
      });
      
      toast.success('Elan uğurla yeniləndi');
      setEditModalOpen(false);
      setSelectedListing(null);
      await loadMyListings();
      if (onRefreshListings) onRefreshListings();
    } catch (error) {
      console.error('Elan yeniləmə xətası:', error);
      toast.error('Elanı yeniləmək mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  // Elanı sil
  const handleDeleteListing = async () => {
    if (!selectedListing?.id) return;

    try {
      setSaving(true);
      await listingService.delete(selectedListing.id);
      
      toast.success('Elan uğurla silindi');
      setDeleteModalOpen(false);
      setSelectedListing(null);
      await loadMyListings();
      if (onRefreshListings) onRefreshListings();
    } catch (error) {
      console.error('Elan silmə xətası:', error);
      toast.error('Elanı silmək mümkün olmadı');
    } finally {
      setSaving(false);
    }
  };

  const ListingCard = ({ listing }) => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="aspect-video bg-gray-100 relative">
        {listing.images?.[0] ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title || listing.type} 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <Image size={48} />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            listing.is_active ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {listing.is_active ? 'Aktiv' : 'Gözləmədə'}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1">{listing.title || listing.type || 'Elan'}</h3>
        <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
          <MapPin size={14} />
          <span className="truncate">{listing.location || 'Məkan göstərilməyib'}</span>
        </div>
        <div className="flex items-center gap-1 text-lg font-bold text-[#B91C1C] mb-3">
          <DollarSign size={18} />
          <span>{listing.price?.toLocaleString() || 0} AZN</span>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={() => openEditModal(listing)}
          >
            <Edit3 size={14} className="mr-1" /> Düzəliş et
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => openDeleteModal(listing)}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/">
                <ArrowLeft size={18} className="mr-2" />
                Geri
              </Link>
            </Button>
            <span className="logo-text text-2xl font-semibold text-[#B91C1C]">Mydom</span>
          </div>
          <Link to="/create-listing">
            <Button className="bg-[#B91C1C] hover:bg-[#991B1B]">
              <Plus size={18} className="mr-2" />
              Yeni Elan
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Başlıq */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Əmlakçı Kabineti</h1>
          <p className="text-gray-600">Xoş gəldiniz, {user?.name || 'İstifadəçi'}</p>
          {userAgency && (
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
              <Building2 size={16} />
              <span>{userAgency.name}</span>
            </div>
          )}
        </div>

        {/* Statistika */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ListChecks size={18} />
              <span className="font-medium">Ümumi Elanlar</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{myListings.length}</p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ListChecks size={18} className="text-green-600" />
              <span className="font-medium">Aktiv Elanlar</span>
            </div>
            <p className="text-3xl font-bold text-green-600">
              {myListings.filter(l => l.is_active).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <ListChecks size={18} className="text-yellow-600" />
              <span className="font-medium">Gözləmədə</span>
            </div>
            <p className="text-3xl font-bold text-yellow-600">
              {myListings.filter(l => !l.is_active).length}
            </p>
          </div>
        </div>

        {/* Elanlar Siyahısı */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Mənim Elanlarım</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B91C1C] mx-auto mb-4"></div>
              <p className="text-gray-500">Yüklənir...</p>
            </div>
          ) : myListings.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border">
              <Image size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">Hələ heç bir elanınız yoxdur</p>
              <Link to="/create-listing">
                <Button className="bg-[#B91C1C] hover:bg-[#991B1B]">
                  <Plus size={18} className="mr-2" />
                  İlk Elanı Yarat
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Düzəliş Modalı */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Elanı Düzəliş Et</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Başlıq</Label>
              <Input
                value={editForm.title || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Elan başlığı"
              />
            </div>
            <div className="space-y-2">
              <Label>Təsvir</Label>
              <Textarea
                value={editForm.description || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Elan təsviri"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Kateqoriya</Label>
                <Select
                  value={editForm.category || 'sale'}
                  onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Satış</SelectItem>
                    <SelectItem value="rent">Kirayə</SelectItem>
                    <SelectItem value="daily">Günlük</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Qiymət (AZN)</Label>
                <Input
                  type="number"
                  value={editForm.price || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Otaq Sayı</Label>
                <Input
                  type="number"
                  value={editForm.rooms || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, rooms: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Sahə (m²)</Label>
                <Input
                  type="number"
                  value={editForm.area || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, area: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mərtəbə</Label>
                <Input
                  type="number"
                  value={editForm.floor || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, floor: e.target.value }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Ümumi Mərtəbə</Label>
                <Input
                  type="number"
                  value={editForm.total_floors || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, total_floors: e.target.value }))}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Məkan</Label>
              <Input
                value={editForm.location || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Şəhər, rayon"
              />
            </div>
            <div className="space-y-2">
              <Label>Ünvan</Label>
              <Input
                value={editForm.address || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Tam ünvan"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={editForm.phone || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+994 XX XXX XX XX"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Ləğv et
            </Button>
            <Button 
              className="bg-[#B91C1C] hover:bg-[#991B1B]"
              onClick={handleUpdateListing}
              disabled={saving}
            >
              {saving ? 'Yüklənir...' : 'Yadda Saxla'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silmə Təsdiq Modalı */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Elanı Sil</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Bu elanı silmək istədiyinizə əminsiniz? Bu əməliyyat geri qaytarıla bilməz.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Ləğv et
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteListing}
              disabled={saving}
            >
              {saving ? 'Silinir...' : 'Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

